import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { courseApi } from 'configs/api';

export interface CourseModule {
  id?: string;
  courseID?: string;
  title: string;
  description?: string;
  order: number;
  isUnlocked: boolean;
  unlockCondition?: 'sequential' | 'assessment_passed' | 'manual';
  content: ModuleContent[] | [];
  
}

export interface ModuleContent {
  id?: string;
  moduleID?: string;
  title: string;
  // type: 'video' | 'image_audio' | 'text' | 'document';
  type: "video" | "image_audio";
  videoURL?: { lang: string; url: string }[];
  imageURL?: string | { lang: string; url: string }[];
  audioURL?: string | { lang: string; url: string }[];
  content?: string;
  order: number;
  // Duration is represented in seconds.
  duration?: number;
  durationSeconds?: number;
}

export interface Course {
  id: string;
  title: string;
  description?: string;
  content?: string;
  categoryID?: string;
  categoryIDs?: string[];
  courseLanguages?: string[];
  status: 'draft' | 'published' | 'archived';
  instructorID: string;
  companyID?: string;
  addedByUserID?: string;
  /** Resolved display name from user service (GetCourseById). */
  addedByUserName?: string;
  price?: number;
  isFree?: boolean;
  quotaPricing?: Record<string, number>;
  instructorName?: string;
  thumbnailURL?: string;
  enrollmentCount?: number;
  // Total course duration is represented in seconds.
  totalDuration?: number;
  totalDurationSeconds?: number;
  moduleCount?: number; // Number of modules in the course
  quotaInfo?: {
    quota: number;
    quotaUsed: number;
    quotaAvailable: number;
  };
  purchaseInfo?: {
    hasAccess: boolean;
    quotaPurchased: number;
    quotaUsed: number;
    quotaAvailable: number;
  };
  freeAssignmentInfo?: {
    quota: number;
    quotaUsed: number;
    quotaAvailable: number;
  };
  assignmentIsFreeAccess?: boolean;
  rating?: number;
  reviewCount?: number;
  modules?: CourseModule[];
  createdAt?: string;
  updatedAt?: string;
  visibilitySource?: string;
}

export interface CourseEnrollment {
  id: string;
  courseID: string;
  userID: string;
  progress?: number;
  status?: string;
  currentModuleID?: string;
  lastAccessedAt?: string;
  completedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  course?: Course;
  enrolledAt?: string;
}

interface CourseState {
  courses: Course[];
  enrollments: CourseEnrollment[];
  currentCourse: Course | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const initialState: CourseState = {
  courses: [],
  enrollments: [],
  currentCourse: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
};

// Async thunks
export const fetchCourses = createAsyncThunk(
  'courses/fetchCourses',
  async ({ page = 1, limit = 10, instructorID, status, visibility, companyID, branchID }: { page?: number; limit?: number; instructorID?: string; status?: string; visibility?: string; companyID?: string; branchID?: string }) => {
    const params: any = { page, limit };
    if (instructorID) params.instructorID = instructorID;
    if (status) params.status = status;
    if (visibility) params.visibility = visibility;
    if (companyID) params.companyID = companyID;
      if (branchID) params.branchID = branchID;
    const response = await courseApi.get('/api/courses', { params });
    return response.data;
  }
);

export const fetchCourse = createAsyncThunk(
  'courses/fetchCourse',
  async (id: string) => {
    const response = await courseApi.get(`/api/courses/${id}`);
    return response.data;
  }
);

export const createCourse = createAsyncThunk(
  'courses/createCourse',
  async (courseData: Partial<Course>) => {
    const response = await courseApi.post('/api/courses', courseData);
    return response.data;
  }
);

export const updateCourse = createAsyncThunk(
  'courses/updateCourse',
  async ({ id, data }: { id: string; data: Partial<Course> }) => {
    const response = await courseApi.put(`/api/courses/${id}`, data);
    return response.data;
  }
);

export const deleteCourse = createAsyncThunk(
  'courses/deleteCourse',
  async (id: string) => {
    await courseApi.delete(`/api/courses/${id}`);
    return id;
  }
);

export const enrollCourse = createAsyncThunk(
  'courses/enrollCourse',
  async (courseId: string) => {
    const response = await courseApi.post(`/api/courses/${courseId}/enroll`);
    return response.data;
  }
);

export const fetchMyEnrollments = createAsyncThunk(
  'courses/fetchMyEnrollments',
  async (filters?: { companyID?: string; branchID?: string; employeeID?: string }) => {
    const response = await courseApi.get('/api/courses/my-enrollments', { params: filters || {} });
    return response.data;
  }
);

const courseSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCourse: (state) => {
      state.currentCourse = null;
    },
    setCurrentCourse: (state, action: PayloadAction<Course | null>) => {
      state.currentCourse = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch courses
      .addCase(fetchCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        const courses = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : payload?.data?.data || [];
        state.courses = courses;
        state.pagination = {
          page: payload?.page ?? payload?.data?.page ?? 1,
          limit: payload?.limit ?? payload?.data?.limit ?? 10,
          total: payload?.total ?? payload?.data?.total ?? 0,
          totalPages: payload?.totalPages ?? payload?.data?.totalPages ?? 0,
        };
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch courses';
      })
      // Fetch single course
      .addCase(fetchCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCourse = action.payload && typeof action.payload === 'object' && 'data' in action.payload
          ? action.payload.data
          : action.payload;
      })
      .addCase(fetchCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch course';
      })
      // Create course
      .addCase(createCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.courses.unshift(action.payload.data);
      })
      .addCase(createCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create course';
      })
      // Update course
      .addCase(updateCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCourse.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.courses.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {

          state.courses[index] = action.payload;
        }
        // if (state.currentCourse?.id === action.payload.id) {
          state.currentCourse = action.payload;
        // }
      })
      .addCase(updateCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update course';
      })
      // Delete course
      .addCase(deleteCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = state.courses.filter(c => c.id !== action.payload);
        if (state.currentCourse?.id === action.payload) {
          state.currentCourse = null;
        }
      })
      .addCase(deleteCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete course';
      })
      // Enroll course
      .addCase(enrollCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enrollCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.enrollments.push(action.payload.data);
      })
      .addCase(enrollCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to enroll in course';
      })
      // Fetch enrollments
      .addCase(fetchMyEnrollments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyEnrollments.fulfilled, (state, action) => {
        state.loading = false;
        state.enrollments = action.payload.data || [];
      })
      .addCase(fetchMyEnrollments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch enrollments';
      });
  },
});

export const { clearError, clearCurrentCourse, setCurrentCourse } = courseSlice.actions;
export default courseSlice.reducer;
